import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  FiHome, FiUsers, FiCheckSquare, FiUser, FiLogOut, 
  FiMenu, FiX, FiSettings, FiLock, FiMoon, FiSun, FiClipboard
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import PasswordUpdateModal from './PasswordUpdateModal';

const Layout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/', icon: FiHome, label: 'Dashboard' },
    { path: '/teams', icon: FiUsers, label: 'Teams' },
    { path: '/tasks', icon: FiCheckSquare, label: 'Tasks' },
    { path: '/my-tasks', icon: FiClipboard, label: 'My Tasks' },
    ...(isAdmin ? [{ path: '/users', icon: FiUser, label: 'Users' }] : []),
  ];

  return (
    <div className={`min-h-screen overflow-x-hidden transition-colors duration-300 ${
      isDark 
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' 
        : 'bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700'
    }`}>
      {/* Mobile Header - Only visible on mobile */}
      <div className={`lg:hidden backdrop-blur-md sticky top-0 z-50 shadow-lg ${
        isDark 
          ? 'bg-gray-800/90 border-b border-gray-700' 
          : 'bg-white/10 border-b border-white/20'
      }`}>
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-2">
            <img 
              src="/logo.png" 
              alt="TaskMate Logo" 
              className="h-14 w-14 object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <h1 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-white'}`}>TaskMate</h1>
          </div>
          <div className="flex items-center space-x-2">
            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className={`relative p-2 rounded-lg transition-all touch-manipulation ${
                isDark
                  ? 'bg-gray-700 hover:bg-gray-600 text-yellow-400'
                  : 'bg-white/10 hover:bg-white/20 text-yellow-300'
              }`}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <FiSun size={20} /> : <FiMoon size={20} />}
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`p-2 -mr-2 touch-manipulation ${isDark ? 'text-gray-300' : 'text-white'}`}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu - Slide in overlay, only shows when menu button is clicked */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
              className="lg:hidden fixed inset-0 bg-black/50 z-40"
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="lg:hidden fixed inset-y-0 left-0 w-72 max-w-[85vw] bg-white z-50 shadow-2xl"
            >
              <div className="h-full flex flex-col overflow-y-auto">
                <div className="p-5 border-b border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <img 
                        src="/logo.png" 
                        alt="TaskMate Logo" 
                        className="h-14 w-14 object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <h2 className="text-xl font-bold text-gray-800">Menu</h2>
                    </div>
                    <button
                      onClick={() => setMobileMenuOpen(false)}
                      className="text-gray-600 p-2 -mr-2 touch-manipulation"
                      aria-label="Close menu"
                    >
                      <FiX size={24} />
                    </button>
                  </div>
                  <div className="px-2 py-2 bg-gray-50 rounded-lg">
                    <p className="text-sm font-semibold text-gray-800">{user?.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{user?.email}</p>
                  </div>
                </div>
                <nav className="flex-1 p-4 space-y-1">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center space-x-3 p-4 rounded-xl transition-all touch-manipulation ${
                          isActive
                            ? 'bg-primary-600 text-white shadow-lg'
                            : 'text-gray-700 active:bg-gray-100'
                        }`}
                      >
                        <Icon size={22} />
                        <span className="font-medium text-base">{item.label}</span>
                      </Link>
                    );
                  })}
                </nav>
                <div className="p-4 border-t border-gray-200 space-y-2">
                  <button
                    onClick={toggleTheme}
                    className="w-full flex items-center justify-center space-x-2 p-4 rounded-xl text-gray-700 bg-gray-100 active:bg-gray-200 transition-all touch-manipulation font-medium"
                  >
                    {isDark ? <FiSun size={20} /> : <FiMoon size={20} />}
                    <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowPasswordModal(true);
                      setMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center justify-center space-x-2 p-4 rounded-xl text-blue-600 bg-blue-50 active:bg-blue-100 transition-all touch-manipulation font-medium"
                  >
                    <FiLock size={20} />
                    <span>Update Password</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center space-x-2 p-4 rounded-xl text-red-600 bg-red-50 active:bg-red-100 transition-all touch-manipulation font-medium"
                  >
                    <FiLogOut size={20} />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Desktop Layout Container */}
      <div className="flex min-h-screen">
        {/* Desktop Sidebar - Completely hidden on mobile, only shows on lg screens and above */}
        <aside className={`hidden lg:flex w-64 flex-shrink-0 backdrop-blur-md min-h-screen sticky top-0 ${
          isDark 
            ? 'bg-gray-800/90 border-r border-gray-700' 
            : 'bg-white/10 border-r border-white/20'
        }`}>
          <div className="flex flex-col w-full">
            <div className="p-6 pt-6">
              <div className="flex items-center space-x-3 mb-6">
                <img 
                  src="/logo.png" 
                  alt="TaskMate Logo" 
                  className="h-16 w-16 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-white'}`}>TaskMate</h1>
              </div>
              <nav className="space-y-2">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center space-x-3 p-3 rounded-lg transition-all ${
                        isActive
                          ? isDark
                            ? 'bg-blue-600 text-white shadow-lg'
                            : 'bg-white/20 text-white shadow-lg'
                          : isDark
                            ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                            : 'text-white/80 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <Icon size={20} />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  );
                })}
              </nav>
            </div>
            <div className="mt-auto p-6 border-t border-white/20">
              <div className="mb-4">
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-white'}`}>{user?.name}</p>
                <p className={`text-xs ${isDark ? 'text-gray-300' : 'text-white/70'}`}>{user?.email}</p>
                <p className={`text-[10px] mt-1 ${isDark ? 'text-gray-500' : 'text-white/40'}`} title="Bump this on deploy to verify cache">v{process.env.REACT_APP_BUILD_ID || '1.0'}</p>
              </div>
              <div className="space-y-2">
                <button
                  onClick={() => setShowPasswordModal(true)}
                  className={`w-full flex items-center justify-center space-x-2 p-3 rounded-lg transition-all ${
                    isDark
                      ? 'bg-blue-600/30 text-white hover:bg-blue-600/40'
                      : 'bg-blue-500/20 text-white hover:bg-blue-500/30'
                  }`}
                >
                  <FiLock size={18} />
                  <span>Update Password</span>
                </button>
                <button
                  onClick={handleLogout}
                  className={`w-full flex items-center justify-center space-x-2 p-3 rounded-lg transition-all ${
                    isDark
                      ? 'bg-red-600/30 text-white hover:bg-red-600/40'
                      : 'bg-red-500/20 text-white hover:bg-red-500/30'
                  }`}
                >
                  <FiLogOut size={18} />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content - Full width on mobile, with sidebar on desktop */}
        <main className="flex-1 w-full px-4 lg:pl-3 lg:pr-6 pt-4 lg:pt-6 pb-4 lg:pb-6 min-w-0 relative">
          {/* Desktop Theme Toggle - Top Right */}
          <div className="hidden lg:block fixed top-6 right-8 z-40">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleTheme}
              className={`group relative p-3.5 rounded-full shadow-2xl transition-all duration-300 ${
                isDark
                  ? 'bg-gradient-to-br from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400'
                  : 'bg-gradient-to-br from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500'
              }`}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <div className="relative z-10">
                {isDark ? (
                  <FiSun size={24} className="text-white" />
                ) : (
                  <FiMoon size={24} className="text-white" />
                )}
              </div>
              {/* Glow effect */}
              <div className={`absolute inset-0 rounded-full blur-xl opacity-60 animate-pulse ${
                isDark
                  ? 'bg-yellow-400'
                  : 'bg-indigo-400'
              }`}></div>
            </motion.button>
          </div>
          {children}
        </main>
      </div>

      {/* Password Update Modal */}
      <AnimatePresence>
        {showPasswordModal && (
          <PasswordUpdateModal onClose={() => setShowPasswordModal(false)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Layout;
